/**
 * Converts an OpenAI-style function call JSON schema into a FormKit JSON schema.
 * @param {Object} schema - The OpenAI function schema (JSON Schema format).
 * @returns {Array} FormKit schema array for use in <FormKitSchema>.
 */
export function convertOpenAIToFormKit(schema) {
    // Helper function to convert a single schema field (property) into a FormKit node
    function convertField(fieldName, fieldSchema, requiredFields) {
        const node = {};
        const fieldType = fieldSchema.type;

        // Determine if this field is required
        const isRequired = Array.isArray(requiredFields) && requiredFields.includes(fieldName);

        // Map basic types to FormKit inputs
        if (fieldType === 'string') {
            node.$formkit = 'text';  // text input for strings [oai_citation:5‡formkitjs.com](https://www.formkitjs.com/inputs/text#:~:text=The%20,a%20single%20line%20of%20text)
        } else if (fieldType === 'number' || fieldType === 'integer') {
            node.$formkit = 'number';  // number input for numbers [oai_citation:6‡formkit.com](https://formkit.com/inputs/number#:~:text=The%20,single%20integer%20or%20decimal%20value)
        } else if (fieldType === 'boolean') {
            node.$formkit = 'checkbox';  // checkbox input for booleans [oai_citation:7‡formkit.com](https://formkit.com/inputs/checkbox#:~:text=Single%20checkbox)
        } else if (fieldType === 'object') {
            node.$formkit = 'group';  // group for nested object
        } else if (fieldType === 'array') {
            node.$formkit = 'list';   // list for arrays
        }

        // Set the field name (omit if this is a list item child with no name)
        if (fieldName) {
            node.name = fieldName;
        }
        // Apply label and help text from title/description
        if (fieldSchema.title) {
            node.label = fieldSchema.title;
        }
        if (fieldSchema.description) {
            node.help = fieldSchema.description;
        }
        // Apply default value if present
        if (fieldSchema.hasOwnProperty('default')) {
            node.value = fieldSchema.default;
        }
        // Mark as required if applicable
        if (isRequired) {
            node.validation = 'required';
        }

        // Handle nested types recursively
        if (fieldType === 'object' && fieldSchema.properties) {
            // Recursively convert each property of the object
            const childRequired = fieldSchema.required || [];
            node.children = Object.keys(fieldSchema.properties).map(propName => {
                const propSchema = fieldSchema.properties[propName];
                return convertField(propName, propSchema, childRequired);
            });
        }
        else if (fieldType === 'array' && fieldSchema.items) {
            // For arrays, create one child node based on the items schema
            const itemSchema = fieldSchema.items;
            node.children = [];
            if (typeof itemSchema.type === 'string') {
                // Recursively convert the item schema. Use an empty name so list children are indexed.
                const itemNode = convertField('', itemSchema, itemSchema.required || []);
                // Remove any name property from item node to rely on index naming (if present).
                delete itemNode.name;
                node.children.push(itemNode);
            } else {
                // If items schema is more complex (e.g., a oneOf or anyOf), handle accordingly (not covered here).
                node.children.push({});  // placeholder for complex schemas
            }
        }

        return node;
    }

    // The root schema is expected to be an object (OpenAI parameters object)
    if (schema.type !== 'object' || !schema.properties) {
        throw new Error('Root schema must be an object with properties');
    }
    const rootRequired = schema.required || [];
    // Convert each top-level property into a FormKit node
    const formkitNodes = Object.keys(schema.properties).map(propName => {
        const propSchema = schema.properties[propName];
        return convertField(propName, propSchema, rootRequired);
    });
    return formkitNodes;
}
